import React, { useEffect, useRef, useState } from "react";
import "./index.css";

import ReactFullpage, { fullpageApi, Item } from "@fullpage/react-fullpage";
import StepOne from "../../layouts/StepOne";
import StepTwo from "../../layouts/StepTwo";
import StepThree from "../../layouts/StepThree";
import StepFour from "../../layouts/StepFour";
import CustomerDetails from "../../models/customerDetails";
import i18nEnglish from "../../i18n/en";
import i18nSwiss from "../../i18n/de-ch";
import i18nFrench from "../../i18n/fr";
import i18nItalian from "../../i18n/it";
import {
  REACT_FULLPAGE_KEY,
  roofTypesMap,
  SOURCE,
  supportedLanguages,
} from "../../config/config";
import useDrafts from "../../hooks/useDrafts";
import httpService from "../../services/httpService";
import TeaserStep from "../../layouts/TeaserStep";
import CookieConsentBanner from "../../components/CookieConsent";
import TopBar from "../../components/TopBar";
import BottomBar from "../../components/BottomBar";
import VerticalProgressBar from "../../components/VerticalProgressBar";

type Trigger =
  | "slideArrow"
  | "verticalNav"
  | "horizontalNav"
  | "keydown"
  | "wheel"
  | "menu";

const SolarCalculator = () => {
  const {
    selectedDraft,
    roofDrafts,
    changeSelectedDraft,
    typeaheadRef,
    updateDraft,
    toggleCustomMode,
    handleSearchAddress,
    selectRoofType,
    onClearRoofSelection,
    onUpdateRoofAddress,
    onSelectAllBuildingRoofs,
    addDraft,
    removeDraftWithUndo,
    undoRemoveDraft,
    dismissUndo,
    pendingDeletion,
    isDraftValid,
    handleCurrentInputChange,
    handleChangingTabs,
    isChangingTabs,
    addressChanged,
    canAddMoreRoofs,
    maxRoofs,
  } = useDrafts();

  const i18nMap =
    {
      de: i18nSwiss,
      "de-ch": i18nSwiss,
      "de-CH": i18nSwiss,
      gsw: i18nSwiss,
      en: i18nEnglish,
      fr: i18nFrench,
      it: i18nItalian,
    }[
      document.documentElement.getAttribute("data-language") ||
        supportedLanguages[0]
    ] || i18nEnglish;

  useEffect(() => {
    document.title = i18nMap.title;
  }, [i18nMap.title]);

  // Step 4 state
  const [selectedOfferings, setSelectedOfferings] = useState<string[]>([]);
  const addOrRemoveOffering = (offeringName: string) => {
    setSelectedOfferings((prevArray) => {
      return prevArray.includes(offeringName)
        ? prevArray.filter((item) => item !== offeringName)
        : [...prevArray, offeringName];
    });
  };

  // Step 5 state
  const [isDataSubmitting, setDataSubmitting] = useState<boolean>(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [captchaToken, setCaptchaToken] = useState();
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({
    surname: "",
    firstName: "",
    email: "",
    phone: "",
    comment: "",
    customerAddress: selectedDraft?.label
      ? {
          street: selectedDraft?.street,
          postalCode: selectedDraft?.zip,
          city: selectedDraft?.city,
          state: selectedDraft?.state,
        }
      : {
          street: "",
          postalCode: "",
          city: "",
          state: "",
        },
  } as CustomerDetails);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, dataset } = e.target;

    if (value?.length > 0) removeError(name);

    if (dataset.group === "address") {
      setCustomerDetails((prevState) => ({
        ...prevState,
        customerAddress: {
          ...prevState.customerAddress,
          [name]: value,
        },
      }));
    } else {
      setCustomerDetails((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const addError = (key: string, message: string) => {
    setErrors((prevErrors) => new Map(prevErrors).set(key, message));
  };

  const removeError = (key: string) => {
    setErrors((prevErrors) => {
      const newErrors = new Map(prevErrors);
      newErrors.delete(key);
      return newErrors;
    });
  };

  const [isAddressSame, setIsAddressSame] = useState<boolean>(false);
  const hasUserInteraction = useRef(false);
  const toggleAddressCheck = () => {
    hasUserInteraction.current = true;
    setIsAddressSame(!isAddressSame);
  };

  useEffect(() => {
    setCustomerDetails((prevState) => ({
      ...prevState,
      customerAddress:
        isAddressSame && selectedDraft?.label
          ? {
              street: selectedDraft.street,
              postalCode: selectedDraft.zip,
              city: selectedDraft.city,
              state: selectedDraft.state,
            }
          : {
              street: prevState.customerAddress.street ?? "",
              postalCode: prevState.customerAddress.postalCode ?? "",
              city: prevState.customerAddress.city ?? "",
              state: prevState.customerAddress.state ?? "",
            },
    }));

    if (
      !hasUserInteraction.current &&
      !selectedDraft.hasCustomRoof &&
      selectedDraft.searchAddress !== null
    ) {
      setIsAddressSame(true);
    }

    if (
      isAddressSame &&
      (selectedDraft.hasCustomRoof || selectedDraft.searchAddress === null)
    ) {
      setIsAddressSame(false);
    }
  }, [selectedDraft, isAddressSame]);

  // Calculate section number for a given roof index and step type
  const getSectionNumber = (roofIndex: number, stepType: 'address' | 'type') => {
    // Each roof has 2 sections: address and type
    const baseSection = roofIndex * 2 + 1; // 1-indexed
    return stepType === 'address' ? baseSection : baseSection + 1;
  };

  // Handle adding a new roof - navigate to the new roof's address section
  const handleAddRoof = (fullpageApi: fullpageApi) => {
    if (canAddMoreRoofs()) {
      addDraft();
      // Navigate to the new roof's address section after a short delay
      // (to allow React to render the new section)
      setTimeout(() => {
        const newRoofSection = getSectionNumber(roofDrafts.length, 'address');
        fullpageApi.moveTo(newRoofSection);
      }, 100);
    }
  };

  // Handle removing a roof - soft delete with undo
  const handleRemoveRoof = (draftId: number, fullpageApi: fullpageApi) => {
    removeDraftWithUndo(draftId);
    setTimeout(() => {
      fullpageApi.moveTo(1);
    }, 100);
  };

  // Handle undo - restore roof and navigate to it
  const handleUndoRemove = () => {
    undoRemoveDraft();
    setTimeout(() => {
      const api = (window as any).fullpage_api;
      if (api && pendingDeletion) {
        const section = getSectionNumber(pendingDeletion.index, 'address');
        api.moveTo(section);
      }
    }, 100);
  };

  const onSubmitData = (fullpageApi: fullpageApi) => {
    dismissUndo();
    if (i18nMap) {
      let hasErrors = false;
      let errs = new Map(errors);
      roofDrafts.forEach((draft, index) => {
        if (!isDraftValid(draft)) {
          errs.set(`${draft.id}:left`, i18nMap.pleaseEnter);
        } else errs.delete(`${draft.id}:left`);
      });

      customerDetails.firstName.length === 0
        ? errs.set("firstName", i18nMap.mandatoryField)
        : errs.delete("firstName");
      customerDetails.surname.length === 0
        ? errs.set("surname", i18nMap.mandatoryField)
        : errs.delete("surname");
      customerDetails.email.length === 0
        ? errs.set("email", i18nMap.mandatoryField)
        : errs.delete("email");
      customerDetails.phone.length === 0
        ? errs.set("phone", i18nMap.mandatoryField)
        : errs.delete("phone");

      !isAddressSame && customerDetails.customerAddress.street.length === 0
        ? errs.set("street", i18nMap.mandatoryField)
        : errs.delete("street");
      !isAddressSame && customerDetails.customerAddress.postalCode.length === 0
        ? errs.set("postalCode", i18nMap.mandatoryField)
        : errs.delete("postalCode");
      !isAddressSame && customerDetails.customerAddress.city.length === 0
        ? errs.set("city", i18nMap.mandatoryField)
        : errs.delete("city");
      !isAddressSame && customerDetails.customerAddress.state.length === 0
        ? errs.set("state", i18nMap.mandatoryField)
        : errs.delete("state");
      !captchaToken
        ? errs.set("captchaToken", i18nMap.invalidCaptcha)
        : errs.delete("captchaToken");

      hasErrors = errs.size !== 0;

      // Find first invalid roof and navigate to it
      for (let i = 0; i < roofDrafts.length; i++) {
        if (!isDraftValid(roofDrafts[i])) {
          fullpageApi.moveTo(getSectionNumber(i, 'address'));
          changeSelectedDraft({}, roofDrafts[i]);
          hasErrors = true;
          break;
        }

        if (roofDrafts[i].roofType === roofTypesMap[0].type) {
          fullpageApi.moveTo(getSectionNumber(i, 'type'));
          changeSelectedDraft({}, roofDrafts[i]);
          hasErrors = true;
          break;
        }
      }
      if (errs.size > 0) {
        window.dataLayer.push({
          event: "formError",
          stepId: activePageId,
          errors: Array.from(errs.entries()),
        });
      }

      setErrors(errs);
      if (!hasErrors && !apiResponse) {
        setDataSubmitting(true);
        const payload = {
          source: SOURCE,
          roofs: roofDrafts.flatMap((a) =>
            a.roofs.map((roof) => ({
              type: a.type,
              roofType: a.roofType,
              area_m2: roof.area,
              orientation_deg: roof.orientation,
              inclination_deg: roof.slope,
              summerElectricityYield_kWh: roof.summerElectricityYield_kWh,
              winterElectricityYield_kWh: roof.winterElectricityYield_kWh,
              financialGain_CHF: roof.financialGain,
              roofAddress: {
                street: a.street ?? "",
                city: a.city ?? "",
                state: a.state ?? "",
                postalCode: a.zip ?? "",
              },
            }))
          ),
          customerDetails: {
            surname: customerDetails.surname,
            firstName: customerDetails.firstName,
            email: customerDetails.email,
            phone: customerDetails.phone,
            customerAddress: {
              street: customerDetails.customerAddress.street,
              city: customerDetails.customerAddress.city,
              state: customerDetails.customerAddress.state,
              postalCode: customerDetails.customerAddress.postalCode,
            },
            sameAsRoofAddress: isAddressSame,
          },
          additionalOfferings: {
            interestedInWallbox: selectedOfferings.includes("wallbox"),
            interestedInBackupPower: selectedOfferings.includes("backupPower"),
          },
          comment: customerDetails.comment,
        };
        window.dataLayer.push({
          event: "formSubmission",
          payload: payload,
        });

        httpService.postData(payload, captchaToken ?? "").then((res: any) => {
          setDataSubmitting(false);
          setApiResponse(res);
        });
      }
    }
  };

  const [activePageId, setActivePageId] = useState<string>("roof-1-address");
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (isScrolling) {
      document.body.classList.add('fp-scrolling');
    } else {
      document.body.classList.remove('fp-scrolling');
    }
  }, [isScrolling]);

  const [lastPageEntry, setLastPageEntry] = useState<number>(Date.now());

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!apiResponse && !isDataSubmitting && activePageId) {
        window.dataLayer.push({
          event: "abandonedFunnel",
          page: activePageId,
          reason: "exit",
          secondsSpent: Math.round((Date.now() - lastPageEntry) / 1000),
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [activePageId, lastPageEntry, isDataSubmitting, apiResponse]);

  const handleLeave = (
    origin: Item,
    destination: Item,
    direction: string,
    trigger: Trigger
  ) => {
    if (
      origin?.item &&
      destination?.item &&
      origin.item.id !== destination.item.id
    ) {
      const secondsElapsed = Math.round((Date.now() - lastPageEntry) / 1000);

      window.dataLayer.push({
        event: "scrollPage",
        direction: direction,
        trigger: trigger,
        fromPage: origin.item.id,
        toPage: destination.item.id,
        secondsElapsedOnPage: secondsElapsed,
      });

      window.dataLayer.push({
        event: "funnelStep",
        stepId: destination.item.id,
        fromStep: origin.item.id,
        direction,
        trigger,
        secondsSpent: secondsElapsed,
      });

      setLastPageEntry(Date.now());
    } else if (
      origin?.item &&
      destination?.item &&
      origin.item.id === destination.item.id
    ) {
      setLastPageEntry(Date.now());
    }
    setIsScrolling(true);

    if (destination && destination.item && destination.item.id) {
      setActivePageId(destination.item.id);

      // Update selected draft based on which roof section we're on
      const match = destination.item.id.match(/^roof-(\d+)-(address|type)$/);
      if (match) {
        const roofIndex = parseInt(match[1]) - 1;
        if (roofDrafts[roofIndex] && roofDrafts[roofIndex].id !== selectedDraft.id) {
          changeSelectedDraft({}, roofDrafts[roofIndex]);
        }
      }
    }
  };

  const handleAfterLoad = () => {
    setIsScrolling(false);
  };

  useEffect(() => {
    const inactivityTimeout = setTimeout(() => {
      if (!apiResponse && !isDataSubmitting && activePageId) {
        window.dataLayer.push({
          event: "abandonedFunnel",
          page: activePageId,
          reason: "inactivity",
          secondsSpent: Math.round((Date.now() - lastPageEntry) / 1000),
        });
      }
    }, 60000);

    return () => clearTimeout(inactivityTimeout);
  }, [activePageId, lastPageEntry, isDataSubmitting, apiResponse]);

  // Detect mobile keyboard open via visualViewport
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      const keyboardOpen = vv.height < window.innerHeight * 0.75;
      document.body.classList.toggle('keyboard-open', keyboardOpen);
    };

    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  const disableFields: boolean = isDataSubmitting || apiResponse !== null;

  // Use roofDrafts.length as key to force ReactFullpage to rebuild when roofs change
  const fullpageKey = `fullpage-${roofDrafts.length}`;

  return (
    <>
      <div className="bg-layers" aria-hidden="true">
        <div className="bg-layer bg-layer-0" />
        <div className="bg-layer bg-layer-1" />
        <div className="bg-layer bg-layer-2" />
        <div className="bg-layer bg-layer-3" />
        <div className="bg-layer bg-layer-4" />
      </div>
      <div id="fullpage-root">
      <TopBar />
      <CookieConsentBanner />
      <VerticalProgressBar
        activeSection={activePageId}
        roofDrafts={roofDrafts}
        selectedDraft={selectedDraft}
        onSelectDraft={(draft) => changeSelectedDraft({}, draft)}
        isDraftValid={isDraftValid}
      />
      <button
        className={`direct-contact-btn ${activePageId === 'roof-1-address' ? 'visible' : 'hidden'}`}
        onClick={() => {
          const api = (window as any).fullpage_api;
          if (api) api.moveTo(roofDrafts.length * 2 + 3);
        }}
      >
        <i className="fa fa-envelope-o" />
        <span>{i18nMap.directContact}</span>
      </button>
      <ReactFullpage
        key={fullpageKey}
        licenseKey={REACT_FULLPAGE_KEY}
        scrollingSpeed={800}
        credits={{}}
        keyboardScrolling={true}
        scrollOverflow={true}
        touchSensitivity={15}
        onLeave={handleLeave}
        afterLoad={handleAfterLoad}
        afterRender={() => {
          document.getElementById('fullpage-root')?.classList.add('fp-ready');
        }}
        render={({ fullpageApi }) => {
          return (
            <ReactFullpage.Wrapper>
              {/* Render sections for each roof */}
              {roofDrafts.map((draft, roofIndex) => (
                <React.Fragment key={draft.id}>
                  {/* Address section for this roof */}
                  <StepOne
                    sectionId={`roof-${roofIndex + 1}-address`}
                    roofIndex={roofIndex}
                    onRemoveRoof={() => handleRemoveRoof(draft.id, fullpageApi)}
                    {...{
                      activePageId,
                      i18nMap,
                      fullpageApi,
                      errors,
                      selectedDraft: draft,
                      roofDrafts,
                      typeaheadRef,
                      updateDraft,
                      toggleCustomMode,
                      handleSearchAddress,
                      onClearRoofSelection,
                      onUpdateRoofAddress,
                      onSelectAllBuildingRoofs,
                      isDraftValid,
                      handleCurrentInputChange,
                      handleChangingTabs,
                      isChangingTabs,
                      disableFields,
                      addressChanged,
                    }}
                  />

                  {/* Roof type section for this roof */}
                  <StepTwo
                    sectionId={`roof-${roofIndex + 1}-type`}
                    roofIndex={roofIndex}
                    isLastRoof={roofIndex === roofDrafts.length - 1}
                    onAddRoof={() => handleAddRoof(fullpageApi)}
                    onRemoveRoof={() => handleRemoveRoof(draft.id, fullpageApi)}
                    {...{
                      activePageId,
                      i18nMap,
                      fullpageApi,
                      errors,
                      selectedDraft: draft,
                      roofDrafts,
                      selectRoofType,
                      addDraft,
                      disableFields,
                      canAddMoreRoofs,
                      maxRoofs,
                    }}
                  />
                </React.Fragment>
              ))}

              {/* Fixed later sections */}
              <TeaserStep
                sectionId="teaser"
                {...{
                  activePageId,
                  i18nMap,
                  fullpageApi,
                  selectedDraft,
                  roofDrafts,
                }}
              />

              <StepThree
                sectionId="offerings"
                {...{
                  activePageId,
                  i18nMap,
                  fullpageApi,
                  selectedOfferings,
                  addOrRemoveOffering,
                  disableFields,
                }}
              />

              <StepFour
                sectionId="contact"
                {...{
                  activePageId,
                  i18nMap,
                  fullpageApi,
                  customerDetails,
                  handleInputChange,
                  isAddressSame,
                  roofAddress: selectedDraft,
                  toggleAddressCheck,
                  errors,
                  addError,
                  removeError,
                  onSubmitData,
                  selectedDraft,
                  setCaptchaToken,
                  captchaToken,
                  isDataSubmitting,
                  apiResponse,
                  setApiResponse,
                  disableFields,
                }}
              />
            </ReactFullpage.Wrapper>
          );
        }}
      />
      <BottomBar />
      {pendingDeletion && (
        <div className="undo-holder">
          <div className="undo-btn" onClick={handleUndoRemove}>
            <i className="fa fa-undo" />
            <span className="undo-text">{i18nMap.roof} {i18nMap.removed} — {i18nMap.undo}</span>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default SolarCalculator;
